import React, { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, AreaChart, Area, Legend } from 'recharts';
import axios from 'axios';
import Header from '../components/Header';
import StatBox from '../stats_page/components/StatBox';
import Sidebar from '../stats_page/components/Sidebar';
import GaugeChart from '../stats_page/components/GaugeChart';

const StatsPage = () => {
  const [stats, setStats] = useState({
    tweetsAnalyzed: 42031,
    maliciousTweetsFound: 23917,
    repliesGenerated: 23905,
    activeBots: 1301
  });

  const [graphData, setGraphData] = useState([
    { name: 'Monday', value1: 50, value2: 30 },
    { name: 'Tuesday', value1: 80, value2: 60 },
    { name: 'Wednesday', value1: 60, value2: 40 },
    { name: 'Thursday', value1: 90, value2: 70 },
    { name: 'Friday', value1: 75, value2: 50 },
    { name: 'Saturday', value1: 100, value2: 85 },
    { name: 'Sunday', value1: 85, value2: 65 },
  ]);
  

  const [sentimentData, setSentimentData] = useState([]);
  const [timeframe, setTimeframe] = useState('week');

  // Fake sentiment trend data
  const fakeSentimentData = {
    week: [
      { date: 'Mon', sentiment: 40 },
      { date: 'Tue', sentiment: 50 },
      { date: 'Wed', sentiment: 45 },
      { date: 'Thu', sentiment: 60 },
      { date: 'Fri', sentiment: 55 },
      { date: 'Sat', sentiment: 20 },
      { date: 'Sun', sentiment: 65 }
    ],
    month: [
      { date: 'Week 1', sentiment: 45 },
      { date: 'Week 2', sentiment: 95 },
      { date: 'Week 3', sentiment: 55 },
      { date: 'Week 4', sentiment: 60 }
    ],
    year: [
      { date: 'Jan', sentiment: 40 },
      { date: 'Feb', sentiment: 50 },
      { date: 'Mar', sentiment: 55 },
      { date: 'Apr', sentiment: 45 },
      { date: 'May', sentiment: 60 },
      { date: 'Jun', sentiment: 65 },
      { date: 'Jul', sentiment: 70 },
      { date: 'Aug', sentiment: 75 },
      { date: 'Sep', sentiment: 80 },
      { date: 'Oct', sentiment: 50 },
      { date: 'Nov', sentiment: 90 },
      { date: 'Dec', sentiment: 95 }
    ]
  };

  useEffect(() => {
    // Fetch stats from backend
    axios.get(`${process.env.REACT_APP_API_SERVER_URL}/api/stats`)
      .then(response => {
        setStats(response.data);
        setGraphData(response.data.graphData || []);
      })
      .catch(error => console.error("Error fetching stats:", error));

    // Set default sentiment data
    setSentimentData(fakeSentimentData.week);
  }, []);

  // Function to update sentiment data based on timeframe selection
  const handleTimeframeChange = (timeframe) => {
    setTimeframe(timeframe);
    setSentimentData(fakeSentimentData[timeframe]);
  };

  return (
    <div>
      <Header />
      <div style={styles.pageContainer}>
        <div style={styles.content}>
          <h2 style={styles.title}>Dashboard - The State of Israel</h2>

          {/* Stats Boxes */}
          <div style={styles.statsContainer}>
            {Object.entries(stats).map(([key, value]) => (
              <StatBox key={key} title={formatTitle(key)} value={value} />
            ))}
          </div>

          {/* Charts Section */}
          <div style={styles.chartsContainer}>
            {/* Wide Bar Chart */}
            <div style={styles.chartBox}>
              <ResponsiveContainer width="100%" height={400}>
              <BarChart data={graphData} barCategoryGap="25%">
                <XAxis dataKey="name" tick={{ fill: '#007bff', fontSize: 14 }} />
                <YAxis tick={{ fill: '#007bff', fontSize: 14 }} />
                <Tooltip contentStyle={{ backgroundColor: '#333', color: '#fff' }} />
                <Legend />
                
                {/* First Bar */}
                <Bar dataKey="value1" fill="#007bff" barSize={20} name="Metric 1" />

                {/* Second Bar */}
                <Bar dataKey="value2" fill="#9bcffa" barSize={20} name="Metric 2" />
              </BarChart>
            </ResponsiveContainer>
            </div>

            {/* Sentiment Gauge Chart */}
            <div style={styles.chartBox}>
              <GaugeChart value={88} />
            </div>
          </div>

          {/* Sentiment Trend Area Chart */}
          <div style={styles.fullWidthChart}>
            <h3>Sentiment Trend Over Time</h3>
            <div style={styles.buttonContainer}>
              <button style={timeframe === 'week' ? styles.activeButton : styles.button} onClick={() => handleTimeframeChange('week')}>1 Week</button>
              <button style={timeframe === 'month' ? styles.activeButton : styles.button} onClick={() => handleTimeframeChange('month')}>1 Month</button>
              <button style={timeframe === 'year' ? styles.activeButton : styles.button} onClick={() => handleTimeframeChange('year')}>1 Year</button>
            </div>
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={sentimentData}>
                <XAxis dataKey="date" />
                <YAxis domain={[0, 100]} />
                <Tooltip />
                <Legend />
                <Area type="linear" dataKey="sentiment" stroke="#001eff" fill="#256be6" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Sidebar with latest responses */}
        <Sidebar />
      </div>
    </div>
  );
};

// Format Titles from API Keys
const formatTitle = (key) => {
  return key.replace(/([A-Z])/g, " $1").replace(/^./, str => str.toUpperCase());
};

// Styles
const styles = {
  pageContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    backgroundColor: '#e6f1fa',
    minHeight: '100vh',
    padding: '20px',
  },
  content: {
    flex: 1,
    padding: '20px',
  },
  title: {
    fontSize: '28px',
    textAlign: 'center',
    marginBottom: '20px',
    color: '#595959',
  },
  statsContainer: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
    gap: '20px',
    marginBottom: '0px',
  },
  statBox: {
    backgroundColor: '#ffffff',
    padding: '20px',
    borderRadius: '8px',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
    textAlign: 'center',
  },
  chartsContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: '20px',
    marginTop: '20px',
    marginBottom: '0px',
  },
  chartBox: {
    flex: 1,
    backgroundColor: '#ffffff', // ✅ White chart containers
    padding: '20px',
    borderRadius: '8px',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  fullWidthChart: {
    backgroundColor: '#ffffff', // ✅ White sentiment trend container
    borderRadius: '8px',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
    marginTop: '20px',
    textAlign: 'center',
    color: '#727273',
    padding: '20px',
  },
  sidebar: {
    width: '300px',
    backgroundColor: '#ffffff', // ✅ White sidebar
    padding: '15px',
    borderLeft: '2px solid #ddd',
    height: '100vh',
    position: 'fixed',
    right: 0,
    top: '60px',
    overflowY: 'auto',
    boxShadow: '-2px 0 4px rgba(0, 0, 0, 0.1)',
  },
  buttonContainer: {
    display: 'flex',
    justifyContent: 'left',
    marginBottom: '10px',
  },
  button: {
    margin: '0 5px',
    padding: '8px 16px',
    border: 'none',
    cursor: 'pointer',
    backgroundColor: '#e0e0e0',
    borderRadius: '5px',
  },
  activeButton: {
    margin: '0 5px',
    padding: '8px 16px',
    border: 'none',
    cursor: 'pointer',
    backgroundColor: '#007bff',
    color: 'white',
    borderRadius: '5px',
  }
};

export default StatsPage;
